import { getAuth } from "firebase/auth";
import type { User } from "firebase/auth";
import { getDoc } from "firebase/firestore";
import { getCookie, removeCookie, setCookie } from "./cookie";
import { TOKEN_HASH_KEY, TOKEN_LOGIN_PATH } from "./tokenLogin";
import { useBaseUrl } from "../composables/useBaseUrl";
import { useFirebaseFirestore } from "../composables/useFirebase";
import { SITE_ORIGINS } from "../../../config";

/*
    ร้าน beta (beta_whitelist) : login ครั้งเดียวที่ xtable.co แล้วใช้ app2 ได้เลย

    firebase auth เก็บ session แยกตาม origin login ที่ xtable.co จึงไม่ติดไปถึง app2
    ของเดิม app2 เลยเชื่อ uid ใน cookie `xtable` แทน firebase ทำให้ไม่เคยได้ claim storeId
    และจะเขียน jobchain ไม่ได้เมื่อ RTDB rules ล็อคด้วย claim

    flow
      1. login สำเร็จที่ xtable.co -> เป็นร้าน beta -> ขอ custom token จาก backend
         (POST /users/handoff-token ส่ง ID token ไปพิสูจน์ตัว) -> ไป app2/login-with-token
      2. app2 signInWithCustomToken (utils/tokenLogin.ts) -> ได้ user คนเดิม claim เดิม
      3. ครั้งต่อไปที่เปิด xtable.co -> boot ปกติ -> fetchBetaWhiteLists() ส่งไป app2
         เฉย ๆ ไม่มี token app2 ใช้ session ของตัวเองที่เก็บไว้
    ขอ token ไม่ได้ (endpoint ล่ม / ยังไม่ deploy) -> ส่งไป app2 เฉย ๆ แล้ว login ที่ app2

    cookie `xtable` ไม่ใช่การ login อีกต่อไป ใช้แค่จำว่ามาจาก origin ไหน
    (from) ไว้ให้ปุ่ม logout ที่ app2 ส่งกลับไปออกจาก xtable.co ด้วย
*/

const COOKIE_NAME = "xtable";
const COOKIE_DOMAIN = ".xtable.co";
const COOKIE_DAYS = 30;

// from ใน cookie แก้ได้จากทุก subdomain ของ xtable.co ยอมให้ redirect กลับไปได้แค่โดเมนเรา
const ALLOWED_FROM = /^https:\/\/([a-z0-9-]+\.)*xtable\.co$/;

// โดเมนหลัก / beta มาจาก runtimeConfig.public.siteOrigins (ดู config.ts)
// ปกติเป็นค่า production ตั้งเป็น URL ของ firebase preview channel ได้ตอนทดสอบ
function siteOrigins(): { main: string; beta: string } {
  try {
    const origins = useRuntimeConfig().public.siteOrigins as { main?: string; beta?: string };
    return { main: origins?.main || SITE_ORIGINS.main, beta: origins?.beta || SITE_ORIGINS.beta };
  } catch {
    return { ...SITE_ORIGINS };
  }
}

export function betaOrigin() {
  return siteOrigins().beta;
}

const TOKEN_REQUEST_TIMEOUT_MS = 5 * 1000;

type HandoffCookie = { isBeta?: boolean; uid?: string; storeId?: string; from?: string };

export function readHandoffCookie(): HandoffCookie | null {
  const raw = getCookie(COOKIE_NAME);
  if (!raw) return null;

  try {
    return JSON.parse(raw) || null;
  } catch {
    return null;
  }
}

export function writeHandoffCookie({ uid, storeId }: { uid: string; storeId: string }) {
  const data: HandoffCookie = { isBeta: true, uid, storeId, from: window.location.origin };
  setCookie(COOKIE_NAME, JSON.stringify(data), COOKIE_DAYS, COOKIE_DOMAIN);
}

// ลบทั้งตัวที่ตั้งด้วย domain .xtable.co (ของจริง) และแบบ host-only
export function clearHandoffCookie() {
  removeCookie(COOKIE_NAME, COOKIE_DOMAIN);
  document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
}

export function isBetaSite() {
  return window.location.hostname === new URL(betaOrigin()).hostname;
}

/** หน้า login ของ xtable.co ที่ปุ่ม logout ของ app2 ส่งไป ให้ xtable.co ออกจากระบบด้วย */
export function mainSiteSignOutUrl() {
  // บน preview channel (*.web.app) ตั้ง cookie โดเมน .xtable.co ไม่ได้ ไม่มี from -> ใช้ค่าจาก config
  const from = readHandoffCookie()?.from;
  const origin = from && ALLOWED_FROM.test(from) ? from : siteOrigins().main;
  return `${origin}/login?signout=1`;
}

export async function isBetaWhitelisted(storeId: string): Promise<boolean> {
  if (!storeId) return false;

  try {
    const snap = await getDoc(useFirebaseFirestore().collectionBetaWhiteList(storeId));
    return snap.exists();
  } catch (error) {
    console.warn("[authHandoff] อ่าน beta_whitelist ไม่สำเร็จ ถือว่าไม่ใช่ร้าน beta", error);
    return false;
  }
}

/** custom token ของ user ที่ login อยู่ (null = ขอไม่ได้ ให้ไป login ที่ app2 เอง) */
export async function requestHandoffToken(): Promise<string | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TOKEN_REQUEST_TIMEOUT_MS);

  try {
    const user = getAuth().currentUser;
    if (!user) return null;

    const idToken = await user.getIdToken();
    const res = await fetch(`${useBaseUrl()}/users/handoff-token`, {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}` },
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const body = await res.json();
    return typeof body?.token === "string" ? body.token : null;
  } catch (error) {
    console.warn("[authHandoff] ขอ token ไม่สำเร็จ ส่งไป app2 แบบไม่มี token (ต้อง login ที่ app2)", error);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * เรียกหลัง login สำเร็จที่หน้า /login
 * @returns true เมื่อส่งไป app2 แล้ว (ผู้เรียกไม่ต้องไป "/" ต่อ)
 */
export async function handoffToBetaAfterLogin(user: User, storeId: string | null): Promise<boolean> {
  if (isBetaSite() || window.location.hostname === "localhost") return false;
  if (!storeId || !(await isBetaWhitelisted(storeId))) return false;

  writeHandoffCookie({ uid: user.uid, storeId });

  const token = await requestHandoffToken();
  window.location.replace(
    token
      ? `${betaOrigin()}${TOKEN_LOGIN_PATH}#${TOKEN_HASH_KEY}=${encodeURIComponent(token)}`
      : betaOrigin()
  );
  return true;
}
